/**
 * Zoho Books OAuth 2.0 Handler + API Client
 * 
 * Full OAuth flow: Popup → Auth Code → Token Exchange (Cloud Function) → Auto-Refresh
 * No redirects, no storage-partitioning issues, no client_secret exposure.
 * 
 * MODIFIED: Now reads Zoho credentials from Firestore settings (per-company)
 * instead of hardcoded config.js values.
 */

import { firebaseConfig } from './config';
import { auth, db } from './index';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getZohoSettings } from './settings';

const DC_MAP = {
  com:      { accounts: 'accounts.zoho.com',      books: 'books.zoho.com' },
  eu:       { accounts: 'accounts.zoho.eu',       books: 'books.zoho.eu' },
  in:       { accounts: 'accounts.zoho.in',       books: 'books.zoho.in' },
  'com.cn': { accounts: 'accounts.zoho.com.cn',   books: 'books.zoho.com.cn' },
  'com.au': { accounts: 'accounts.zoho.com.au',   books: 'books.zoho.com.au' },
};

let cachedZohoSettings = null;

/**
 * Load Zoho settings from Firestore (with caching).
 * Falls back to static config for backward compatibility.
 */
const loadZohoSettings = async () => {
  if (cachedZohoSettings) return cachedZohoSettings;

  const fallbackSettings = {
    zohoOrgId: firebaseConfig.zohoOrgId,
    zohoClientId: firebaseConfig.zohoClientId,
    zohoDatacenter: firebaseConfig.zohoDatacenter || 'eu',
    zohoRedirectUri: firebaseConfig.zohoRedirectUri || `${window.location.origin}/zoho-callback`,
    zohoScopes: firebaseConfig.zohoScopes || 'ZohoBooks.contacts.READ,ZohoBooks.estimates.READ,ZohoBooks.invoices.READ',
    zohoFunctionsRegion: firebaseConfig.zohoFunctionsRegion || 'us-central1',
  };

  try {
    const settings = await getZohoSettings();
    if (!settings || !settings.zohoClientId) {
      console.warn('[Zoho] Firestore settings missing/empty, using static config fallback');
      cachedZohoSettings = fallbackSettings;
      return fallbackSettings;
    }
    cachedZohoSettings = settings;
    return settings;
  } catch (e) {
    console.warn('[Zoho] Failed to load settings from Firestore, using static config:', e);
    cachedZohoSettings = fallbackSettings;
    return fallbackSettings;
  }
};

const getDC = async () => {
  const settings = await loadZohoSettings();
  return DC_MAP[settings.zohoDatacenter || 'eu'] || DC_MAP.eu;
};
const ACCOUNTS_BASE = async () => `https://${(await getDC()).accounts}/oauth/v2`;
const BOOKS_BASE = async () => `https://www.zohoapis.${(await getDC()).books.split('.')[1]}/books/v3`;

const isMockMode = async () => {
  const settings = await loadZohoSettings();
  return !settings.zohoClientId || settings.zohoClientId === 'YOUR_ZOHO_CLIENT_ID';
};

const getCloudFunctionUrl = async (functionName) => {
  const settings = await loadZohoSettings();
  const regionAbbr = 'uc'; // us-central1
  const projectHash = 'lgrwjptv5a';
  const projectId = firebaseConfig.projectId || 'YOUR_PROJECT_ID';
  if (projectId === 'YOUR_PROJECT_ID' || projectId === 'REPLACE_WITH_YOUR_FIREBASE_PROJECT_ID') {
    throw new Error('Firebase projectId not configured in firebase/config.js');
  }
  return `https://${functionName.toLowerCase()}-${projectHash}-${regionAbbr}.a.run.app`;
};

const getTokenDocRef = () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return doc(db, 'zohoTokens', user.uid);
};

const loadTokens = async () => {
  const snap = await getDoc(getTokenDocRef());
  return snap.exists() ? snap.data() : null;
};

const saveTokens = async (tokens) => {
  await setDoc(getTokenDocRef(), {
    ...tokens,
    updatedAt: serverTimestamp(),
  }, { merge: true });
};

// ═══════════════════════════════════════════════════════════════
// UTILITY
// ═══════════════════════════════════════════════════════════════

const generateState = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// ═══════════════════════════════════════════════════════════════
// POPUP-BASED OAUTH (no redirects, no storage-partitioning issues)
// ═══════════════════════════════════════════════════════════════

export const initiateZohoOAuth = async () => {
  return new Promise(async (resolve, reject) => {
    if (await isMockMode()) {
      reject(new Error('Zoho OAuth not configured. Set zohoClientId in settings or config.js'));
      return;
    }

    const settings = await loadZohoSettings();
    const redirectUri = settings.zohoRedirectUri || `${window.location.origin}/zoho-callback`;
    const scope = settings.zohoScopes || 'ZohoBooks.contacts.READ,ZohoBooks.estimates.READ';
    const state = generateState();
    const clientId = settings.zohoClientId;
    const baseUrl = `${await ACCOUNTS_BASE()}/auth`;
    
    // Explicitly build URL with all parameters to avoid truncation issues
    const paramPairs = [
      `client_id=${encodeURIComponent(clientId)}`,
      `response_type=code`,
      `scope=${encodeURIComponent(scope)}`,
      `redirect_uri=${encodeURIComponent(redirectUri)}`,
      `access_type=offline`,
      `state=${encodeURIComponent(state)}`,
      `prompt=consent`,
    ];
    
    const authUrl = `${baseUrl}?${paramPairs.join('&')}`;

    console.log('[Zoho OAuth] Full URL:', authUrl);
    console.log('[Zoho OAuth] Redirect URI:', redirectUri);
    console.log('[Zoho OAuth] Scope:', scope);
    console.log('[Zoho OAuth] Client ID:', clientId);

    const width = 500, height = 600;
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;

    const popup = window.open(
      authUrl,
      'zohoOAuth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
    );

    if (!popup) {
      reject(new Error('Popup blocked. Please allow popups for this site.'));
      return;
    }

    let resolved = false;
    let checkClosedInterval;

    const cleanup = () => {
      if (checkClosedInterval) clearInterval(checkClosedInterval);
      window.removeEventListener('message', handleMessage);
      try { popup.close(); } catch (e) {}
    };

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type === 'zoho-auth-success' && data?.code) {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(data.code);
        }
      }
      if (data?.type === 'zoho-auth-error') {
        if (!resolved) {
          resolved = true;
          cleanup();
          reject(new Error(data.error || 'OAuth failed'));
        }
      }
    };

    window.addEventListener('message', handleMessage);

    checkClosedInterval = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true;
        cleanup();
        reject(new Error('Popup was closed before completing authorization.'));
      }
    }, 1000);

    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        reject(new Error('OAuth timed out after 5 minutes.'));
      }
    }, 300000);
  });
};

// ═══════════════════════════════════════════════════════════════
// TOKEN EXCHANGE (via Cloud Function — client_secret never exposed)
// ═══════════════════════════════════════════════════════════════

export const exchangeCodeForTokens = async (code) => {
  if (await isMockMode()) return { accessToken: 'mock', refreshToken: 'mock', expiresAt: Date.now() + 3600000 };

  const settings = await loadZohoSettings();
  const redirectUri = settings.zohoRedirectUri || `${window.location.origin}/zoho-callback`;
  const functionUrl = await getCloudFunctionUrl('zohoOAuthExchange');

  console.log('[Zoho OAuth] Exchanging code for tokens...', { functionUrl, redirectUri });

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, redirectUri, datacenter: settings.zohoDatacenter }),
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    console.error('[Zoho OAuth] Token exchange failed:', data);
    throw new Error(data.message || data.error || 'Token exchange failed');
  }

  const tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in * 1000),
  };

  await saveTokens(tokens);
  return tokens;
};

export const refreshZohoToken = async () => {
  if (await isMockMode()) return { accessToken: 'mock', refreshToken: 'mock', expiresIn: 3600 };

  const stored = await loadTokens();
  if (!stored?.refreshToken) {
    throw new Error('No refresh token available. Please reconnect Zoho.');
  }

  const settings = await loadZohoSettings();
  const functionUrl = await getCloudFunctionUrl('zohoRefreshToken');

  console.log('[Zoho OAuth] Refreshing token...', { functionUrl });

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken: stored.refreshToken, datacenter: settings.zohoDatacenter }),
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    console.error('[Zoho OAuth] Token refresh failed:', data);
    throw new Error(data.message || data.error || 'Token refresh failed');
  }

  const tokens = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token || stored.refreshToken,
    expiresAt: Date.now() + (data.expires_in * 1000),
  };

  await saveTokens(tokens);
  return tokens;
};

export const getValidAccessToken = async () => {
  const stored = await loadTokens();
  if (!stored) return null;

  const fiveMinutes = 5 * 60 * 1000;
  if (Date.now() < stored.expiresAt - fiveMinutes) {
    return stored.accessToken;
  }

  const refreshed = await refreshZohoToken();
  return refreshed.accessToken;
};

// ═══════════════════════════════════════════════════════════════
// CONNECTED CHECK
// ═══════════════════════════════════════════════════════════════

export const isZohoConnected = async () => {
  try {
    const token = await getValidAccessToken();
    return !!token;
  } catch {
    return false;
  }
};

export const connectZohoAccount = async () => {
  try {
    const code = await initiateZohoOAuth();
    const tokens = await exchangeCodeForTokens(code);
    return { success: true, tokens, error: null };
  } catch (error) {
    console.error('[Zoho OAuth Error]', error);
    return { success: false, tokens: null, error: error.message };
  }
};

// ═══════════════════════════════════════════════════════════════
// ZOHO API CALLS (via Cloud Function proxy — avoids CORS)
// ═══════════════════════════════════════════════════════════════

const zohoFetch = async (endpoint, options = {}) => {
  if (await isMockMode()) {
    throw new Error('Mock mode — no real API calls');
  }

  const token = await getValidAccessToken();
  if (!token) {
    throw new Error('Zoho not connected. Please connect your account first.');
  }

  const settings = await loadZohoSettings();
  const orgId = settings.zohoOrgId;
  if (!orgId || orgId === 'YOUR_ZOHO_ORG_ID') {
    throw new Error('Zoho Org ID not configured in settings.');
  }

  const functionUrl = await getCloudFunctionUrl('zohoApiProxy');

  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint,
      accessToken: token,
      orgId,
      method: options.method || 'GET',
      body: options.body,
    }),
  });

  const data = await response.json();

  if (!response.ok || data.error) {
    throw new Error(data.message || data.error || `Zoho API error: ${response.status}`);
  }

  return data;
};

// ═══════════════════════════════════════════════════════════════
// SEARCH CUSTOMERS
// ═══════════════════════════════════════════════════════════════

export const searchZohoCustomers = async (query) => {
  try {
    const data = await zohoFetch(`/contacts?contact_name_contains=${encodeURIComponent(query)}`);
    return { customers: data.contacts || [], error: null };
  } catch (error) {
    return { customers: [], error: error.message };
  }
};

// ═══════════════════════════════════════════════════════════════
// ZOHO ESTIMATES
// ═══════════════════════════════════════════════════════════════

export const getZohoEstimates = async (customerId) => {
  try {
    const data = await zohoFetch(`/estimates?customer_id=${encodeURIComponent(customerId)}`);
    return { estimates: data.estimates || [], error: null };
  } catch (error) {
    return { estimates: [], error: error.message };
  }
};

export const getZohoEstimateDetails = async (estimateId) => {
  try {
    const data = await zohoFetch(`/estimates/${estimateId}`);
    return { estimate: data.estimate, error: null };
  } catch (error) {
    return { estimate: null, error: error.message };
  }
};

export const getZohoInvoices = async (customerId) => {
  try {
    const data = await zohoFetch(`/invoices?customer_id=${encodeURIComponent(customerId)}`);
    return { invoices: data.invoices || [], error: null };
  } catch (error) {
    return { invoices: [], error: error.message };
  }
};

export const getZohoInvoiceDetails = async (invoiceId) => {
  try {
    const data = await zohoFetch(`/invoices/${invoiceId}`);
    return { invoice: data.invoice, error: null };
  } catch (error) {
    return { invoice: null, error: error.message };
  }
};

// ═══════════════════════════════════════════════════════════════
// TRANSFORM ZOHO CONTACT + ESTIMATE / INVOICE → JOB FIELDS
// ═══════════════════════════════════════════════════════════════

export const transformZohoToJob = (contact, doc = null, docType = 'estimate') => {
  const lineItem = doc?.line_items?.[0] || {};
  
  // Title = Subject (line item description) from estimate/invoice
  const title = lineItem.description || contact.contact_name || '';
  
  const job = {
    zohoCustomerId: contact.contact_id || '',
    companyName: contact.contact_name || contact.company_name || '',
    clientContact: contact.contact_name || '',
    clientEmail: contact.email || '',
    clientPhone: contact.phone || contact.mobile || '',
    primarySiteContact: contact.billing_address?.attention || '',
    companyAddress: [
      contact.billing_address?.street1,
      contact.billing_address?.street2,
      contact.billing_address?.city,
      contact.billing_address?.state,
      contact.billing_address?.zip,
      contact.billing_address?.country,
    ].filter(Boolean).join(', ') || contact.billing_address || '',
    jobType: 'General Service',
    jobReference: contact.customer_sub_type || '',
    jobDetails: '',
    additionalInfo: '',
    zohoSynced: true,
    zohoEstimateId: '',
    zohoInvoiceId: '',
    // Title = Subject from estimate/invoice line item description
    title: title,
    // Location = blank (not auto-filled)
    location: '',
  };

  // If we have a document, extract richer data from line items
  if (doc) {
    // Job Type = first line item's sales item name (e.g., "French Polishing")
    job.jobType = lineItem.name || lineItem.item_type || job.jobType;
    
    // Job Details = first line item's description (e.g., "Refinishing 5x smoked veneer doors\nFlat 4")
    job.jobDetails = lineItem.description || doc.description || job.jobDetails;
    
    // Job Reference = document number
    job.jobReference = doc.estimate_number || doc.invoice_number || job.jobReference;
    
    // Zoho Document IDs
    if (docType === 'estimate') {
      job.zohoEstimateId = doc.estimate_id || '';
    } else {
      job.zohoInvoiceId = doc.invoice_id || '';
    }
    
    // Additional info from document terms
    job.additionalInfo = doc.terms || doc.notes || job.additionalInfo;
  }

  return job;
};

// ═══════════════════════════════════════════════════════════════
// MOCK FALLBACK (for development without real Zoho credentials)
// ═══════════════════════════════════════════════════════════════

const MOCK_CUSTOMERS = [
  { contact_id: 'mock-1', contact_name: 'ABC Construction Ltd', email: 'info@abcconstruction.com', phone: '+44 20 1234 5678' },
  { contact_id: 'mock-2', contact_name: 'Bradfield Properties', email: 'contact@bradfield.co.uk', phone: '+44 20 8765 4321' },
  { contact_id: 'mock-3', contact_name: 'City Surfacing Co', email: 'hello@citysurfacing.com', phone: '+44 20 5555 9999' },
];

export const searchZohoCustomersMock = async (query) => {
  await new Promise(r => setTimeout(r, 800));
  const q = query.toLowerCase();
  return {
    customers: MOCK_CUSTOMERS.filter(c => c.contact_name.toLowerCase().includes(q)),
    error: null,
  };
};
